import React from "react";
import { cn } from "@/lib/utils";

interface FileRowProps {
  /** Leading icon, typically a 16px lucide glyph. */
  icon: React.ReactNode;
  label: string;
  /** Small badge after the label, e.g. "Updated" or "CSV". */
  badge?: React.ReactNode;
  /** Second line, e.g. "24 KB · 2 hours ago". */
  sub?: string;
  /** Right-aligned metadata, e.g. a relative timestamp. */
  meta?: string;
  /** Trailing affordance, revealed on hover when the row is interactive. */
  trailing?: React.ReactNode;
  onClick?: () => void;
  /** Renders dimmed and non-interactive, for things that don't exist yet. */
  muted?: boolean;
}

/**
 * One file row, shared by the Deliverables and Uploaded files cards — they have
 * the same shape (icon, name, sub-line, metadata, trailing affordance) and
 * duplicating it would guarantee they drift.
 *
 * Hover uses `bg-accent` rather than `bg-muted`: in dark mode `--muted` is
 * identical to `--background`, so `hover:bg-muted` is invisible.
 */
const FileRow: React.FC<FileRowProps> = ({
  icon,
  label,
  badge,
  sub,
  meta,
  trailing,
  onClick,
  muted = false,
}) => {
  const interactive = Boolean(onClick) && !muted;
  const Element = interactive ? "button" : "div";

  return (
    <Element
      {...(interactive ? { type: "button" as const, onClick } : {})}
      className={cn(
        "group w-full flex items-center gap-3 px-4 py-2.5 text-left",
        interactive && "cursor-pointer hover:bg-accent",
        muted && "opacity-55",
      )}
    >
      <span
        className={cn(
          "flex-none text-muted-foreground",
          interactive &&
            "group-hover:text-blue-600 dark:group-hover:text-blue-400",
        )}
      >
        {icon}
      </span>

      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 text-[13px] font-medium text-foreground">
          <span className="truncate">{label}</span>
          {badge}
        </span>
        {sub && (
          <span className="block truncate text-[11px] text-muted-foreground mt-px">
            {sub}
          </span>
        )}
      </span>

      {meta && (
        <span className="flex-none text-[11.5px] text-muted-foreground whitespace-nowrap">
          {meta}
        </span>
      )}

      {trailing && (
        <span
          className={cn(
            "flex-none text-muted-foreground",
            interactive &&
              "opacity-0 group-hover:opacity-100 group-hover:text-blue-600 dark:group-hover:text-blue-400",
          )}
        >
          {trailing}
        </span>
      )}
    </Element>
  );
};

export default FileRow;
